import type {
  ImpersonationConsumeRequest,
  ImpersonationConsumeResponse,
  LoginRequest,
  SSOConsumeRequest,
  SSOInitiateRequest,
  SSOInitiateResponse,
  TokenPairResponse,
  UserOut,
  WebAuthnCredentialOut,
  WebAuthnLoginBeginResponse,
  WebAuthnLoginCompleteRequest,
  WebAuthnRegisterBeginResponse,
  WebAuthnRegisterCompleteRequest,
} from "@shop-ledger/shared-types";
import { apiRequest } from "../lib/apiClient";

/**
 * `POST /auth/login` with `client: "pos"` — as of the `pos-pwa` merge, every session in
 * this app can end up on a `/pos/*` route (the same login serves both the back office and
 * the till now), so every login gets the long-lived, offline-friendly access token
 * (§2's offline exception) rather than the short-lived `"dashboard"` one. This is safe for
 * pure back-office use too: `ensureFreshToken()` below still proactively refreshes near
 * expiry, it just refreshes less often. A longer-lived token is exactly what an
 * ACCOUNTANT/ECOM_MGR session gets today even though they'll never hit `/pos` — a harmless
 * trade (fewer refresh round-trips) for the simplicity of one login behavior for every
 * role, rather than branching token lifetime on a role the login endpoint doesn't even
 * know yet at the time of the call.
 */
export async function login(email: string, password: string): Promise<TokenPairResponse> {
  const body: LoginRequest = { email, password, client: "pos" };
  return apiRequest<TokenPairResponse>("/auth/login", { method: "POST", body });
}

export async function fetchCurrentUser(accessToken: string): Promise<UserOut> {
  return apiRequest<UserOut>("/auth/me", { accessToken });
}

export async function refreshTokenPair(refreshToken: string): Promise<TokenPairResponse> {
  return apiRequest<TokenPairResponse>("/auth/refresh", { method: "POST", body: { refresh_token: refreshToken } });
}

export async function logout(refreshToken: string): Promise<void> {
  try {
    await apiRequest<void>("/auth/logout", { method: "POST", body: { refresh_token: refreshToken } });
  } catch {
    // Best-effort, same as pos-pwa: an offline/failed logout call still clears the local
    // session; the refresh token simply stays valid server-side until it expires.
  }
}

/** Cross-app SSO handoff, step 1 — this app is always the SOURCE here
 * (asking for a code to hand to `pos-pwa`/`ecommerce-store`'s own `/sso`
 * consume route), never the target (see `authApi.ssoConsume` below for
 * that direction, used by this app's own `/sso` page). */
export async function ssoInitiate(accessToken: string, targetClient: SSOInitiateRequest["target_client"]): Promise<string> {
  const res = await apiRequest<SSOInitiateResponse>("/auth/sso/initiate", {
    method: "POST",
    accessToken,
    body: { target_client: targetClient } satisfies SSOInitiateRequest,
  });
  return res.code;
}

/** Cross-app SSO handoff, step 2 — consumes a code minted by another app
 * (or by `GET /auth/demo/handoff` for the marketing-site "Try the Live
 * Demo" flow) and returns a real session, same shape as `login()`. */
export async function ssoConsume(code: string): Promise<TokenPairResponse> {
  return apiRequest<TokenPairResponse>("/auth/sso/consume", { method: "POST", body: { code } satisfies SSOConsumeRequest });
}

/** Landing side of platform-admin's "View as tenant" impersonation handoff — reached
 * from `/impersonate?code=...` (`ImpersonationConsumePage`). Returns the exact
 * impersonation access token `POST /platform/tenants/{id}/impersonate` already minted,
 * with no refresh token (§9.1's non-renewable-by-design impersonation session). */
export async function impersonationConsume(code: string): Promise<ImpersonationConsumeResponse> {
  return apiRequest<ImpersonationConsumeResponse>("/auth/impersonation/consume", {
    method: "POST",
    body: { code } satisfies ImpersonationConsumeRequest,
  });
}

/** Passkey registration — authenticated (attaching a passkey to your own already-logged-in
 * account). See `@shop-ledger/ui-components`'s `registerPasskey` for the browser-side half. */
export async function webauthnRegisterBegin(accessToken: string): Promise<WebAuthnRegisterBeginResponse> {
  return apiRequest<WebAuthnRegisterBeginResponse>("/auth/webauthn/register/begin", {
    method: "POST",
    accessToken,
  });
}

export async function webauthnRegisterComplete(
  accessToken: string,
  body: WebAuthnRegisterCompleteRequest,
): Promise<WebAuthnCredentialOut> {
  return apiRequest<WebAuthnCredentialOut>("/auth/webauthn/register/complete", {
    method: "POST",
    accessToken,
    body,
  });
}

export async function webauthnListCredentials(accessToken: string): Promise<WebAuthnCredentialOut[]> {
  return apiRequest<WebAuthnCredentialOut[]>("/auth/webauthn/credentials", { accessToken });
}

export async function webauthnDeleteCredential(accessToken: string, credentialId: string): Promise<void> {
  await apiRequest<void>(`/auth/webauthn/credentials/${credentialId}`, { method: "DELETE", accessToken });
}

/** Passkey login — public, usernameless. See `@shop-ledger/ui-components`'s
 * `authenticateWithPasskey` for the browser-side half. */
export async function webauthnLoginBegin(): Promise<WebAuthnLoginBeginResponse> {
  return apiRequest<WebAuthnLoginBeginResponse>("/auth/webauthn/login/begin", { method: "POST" });
}

export async function webauthnLoginComplete(body: WebAuthnLoginCompleteRequest): Promise<TokenPairResponse> {
  return apiRequest<TokenPairResponse>("/auth/webauthn/login/complete", { method: "POST", body });
}
